import { useState, useEffect } from 'react';
import { Plus, FileText, CheckCircle, XCircle, ArrowRight, Search, Trash2, Share2, Copy, Edit, UserPlus, Car, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';

function Orcamentos() {
  const [orcamentos, setOrcamentos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [pecas, setPecas] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingOrcamento, setEditingOrcamento] = useState(null);
  const [searchPeca, setSearchPeca] = useState('');
  const [isClienteDialogOpen, setIsClienteDialogOpen] = useState(false);
  const [novoCliente, setNovoCliente] = useState({
    nome: '',
    cpf_cnpj: '',
    telefone: '',
    email: '',
    endereco: ''
  });
  const [novosVeiculos, setNovosVeiculos] = useState([]);
  const [showVeiculoForm, setShowVeiculoForm] = useState(false);
  const [veiculoForm, setVeiculoForm] = useState({
    placa: '',
    marca: '',
    modelo: '',
    ano: '',
    cor: ''
  });
  const [formData, setFormData] = useState({
    cliente_id: '',
    veiculo_id: '',
    items: [],
    observacoes: '',
    validade: ''
  });
  const [veiculosFiltrados, setVeiculosFiltrados] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    tipo: 'servico',
    descricao: '',
    quantidade: 1,
    valor_unitario: '',
    valor_total: 0,
    desconto_tipo: 'valor', // 'valor' | 'percentual'
    desconto_valor: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [orcamentosRes, clientesRes, veiculosRes, pecasRes] = await Promise.all([
        api.get('/orcamentos'),
        api.get('/clientes'),
        api.get('/veiculos'),
        api.get('/pecas')
      ]);
      setOrcamentos(orcamentosRes.data);
      setClientes(clientesRes.data);
      setVeiculos(veiculosRes.data);
      setPecas(pecasRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    }
  };

  const handleAddVeiculo = () => {
    if (!veiculoForm.placa || !veiculoForm.marca || !veiculoForm.modelo) {
      toast.error('Preencha placa, marca e modelo');
      return;
    }
    setNovosVeiculos([...novosVeiculos, veiculoForm]);
    setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
    setShowVeiculoForm(false);
  };

  const handleRemoveVeiculo = (index) => {
    setNovosVeiculos(novosVeiculos.filter((_, i) => i !== index));
  };

  const handleCadastrarCliente = async (e) => {
    e.preventDefault();
    if (!novoCliente.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      // Cadastrar cliente
      const { data: clienteData } = await api.post('/clientes', novoCliente);
      
      // Cadastrar veículos se houver
      let primeiroVeiculoId = null;
      if (novosVeiculos.length > 0) {
        for (const veiculo of novosVeiculos) {
          const { data: veiculoData } = await api.post('/veiculos', {
            ...veiculo,
            cliente_id: clienteData.id
          });
          if (!primeiroVeiculoId) primeiroVeiculoId = veiculoData.id;
        }
        // Recarregar veículos
        const veiculosRes = await api.get('/veiculos');
        setVeiculos(veiculosRes.data);
        toast.success(`${novosVeiculos.length} veículo(s) cadastrado(s)`);
      }
      
      // Atualizar lista de clientes e selecionar o novo
      setClientes([...clientes, clienteData]);
      
      // Atualizar veículos filtrados
      const veiculosDoCliente = novosVeiculos.length > 0 
        ? novosVeiculos.map((v, i) => ({ ...v, id: primeiroVeiculoId, cliente_id: clienteData.id }))
        : [];
      setVeiculosFiltrados(veiculosDoCliente);
      
      setFormData({ 
        ...formData, 
        cliente_id: clienteData.id,
        veiculo_id: primeiroVeiculoId || ''
      });
      
      // Resetar formulário de cliente
      setNovoCliente({ nome: '', cpf_cnpj: '', telefone: '', email: '', endereco: '' });
      setNovosVeiculos([]);
      setShowVeiculoForm(false);
      setIsClienteDialogOpen(false);
      
      // Recarregar dados
      loadData();
      
      toast.success('Cliente cadastrado com sucesso!');
    } catch (error) {
      toast.error('Erro ao cadastrar cliente');
      console.error(error);
    }
  };

  const handleAddItem = () => {
    const valorUnit = parseFloat(currentItem.valor_unitario) || 0;
    const qtd = currentItem.quantidade || 1;
    const subtotal = qtd * valorUnit;
    // Calcula desconto
    const dv = parseFloat(currentItem.desconto_valor) || 0;
    let descontoAplicado = 0;
    if (currentItem.desconto_tipo === 'percentual') {
      descontoAplicado = Math.min(100, Math.max(0, dv)) / 100 * subtotal;
    } else {
      descontoAplicado = Math.min(subtotal, Math.max(0, dv));
    }
    const valorTotal = Math.max(0, subtotal - descontoAplicado);
    const newItem = {
      ...currentItem,
      _key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      valor_unitario: valorUnit,
      valor_total: valorTotal,
      desconto_tipo: dv > 0 ? currentItem.desconto_tipo : null,
      desconto_valor: dv > 0 ? dv : 0,
    };
    setFormData({ ...formData, items: [...formData.items, newItem] });
    setCurrentItem({ tipo: 'servico', descricao: '', quantidade: 1, valor_unitario: '', valor_total: 0, desconto_tipo: 'valor', desconto_valor: '' });
    setSearchPeca('');
  };

  const handleSelectPeca = (pecaId) => {
    const peca = pecas.find(p => p.id === pecaId);
    if (peca) {
      setCurrentItem({
        tipo: 'peca',
        descricao: peca.nome,
        quantidade: 1,
        valor_unitario: peca.preco_venda,
        valor_total: peca.preco_venda
      });
      setSearchPeca('');
      toast.success(`Peça "${peca.nome}" selecionada`);
    }
  };

  const filteredPecas = pecas.filter(p =>
    searchPeca && (
      p.nome.toLowerCase().includes(searchPeca.toLowerCase()) ||
      p.codigo.toLowerCase().includes(searchPeca.toLowerCase())
    )
  );

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Adicione pelo menos um item ao orçamento');
      return;
    }
    try {
      if (editingOrcamento) {
        await api.put(`/orcamentos/${editingOrcamento.id}`, formData);
        toast.success('Orçamento atualizado com sucesso');
      } else {
        await api.post('/orcamentos', formData);
        toast.success('Orçamento criado com sucesso');
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar orçamento');
      console.error(error);
    }
  };

  const handleEdit = (orcamento) => {
    setEditingOrcamento(orcamento);
    const veiculosDoCliente = veiculos.filter(v => v.cliente_id === orcamento.cliente_id);
    setVeiculosFiltrados(veiculosDoCliente);
    setFormData({
      cliente_id: orcamento.cliente_id,
      veiculo_id: orcamento.veiculo_id || '',
      items: orcamento.items || [],
      observacoes: orcamento.observacoes || '',
      validade: orcamento.validade ? orcamento.validade.split('T')[0] : ''
    });
    setIsDialogOpen(true);
  };

  const handleAprovar = async (id) => {
    try {
      await api.post(`/orcamentos/${id}/aprovar`);
      toast.success('Orçamento aprovado');
      loadData();
    } catch (error) {
      toast.error('Erro ao aprovar orçamento');
      console.error(error);
    }
  };

  const handleRecusar = async (id) => {
    try {
      await api.post(`/orcamentos/${id}/recusar`);
      toast.success('Orçamento recusado');
      loadData();
    } catch (error) {
      toast.error('Erro ao recusar orçamento');
      console.error(error);
    }
  };

  const handleConverter = async (id) => {
    try {
      await api.post(`/orcamentos/${id}/converter`);
      toast.success('Orçamento convertido em Ordem de Serviço');
      loadData();
    } catch (error) {
      toast.error('Erro ao converter orçamento');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este orçamento?')) {
      try {
        await api.delete(`/orcamentos/${id}`);
        toast.success('Orçamento excluído com sucesso');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir orçamento');
        console.error(error);
      }
    }
  };

  const handleClienteChange = (clienteId) => {
    const veiculosDoCliente = veiculos.filter(v => v.cliente_id === clienteId);
    setVeiculosFiltrados(veiculosDoCliente);
    setFormData({ ...formData, cliente_id: clienteId, veiculo_id: '' });
  };

  const handleCopiarOrcamento = (orcamento) => {
    const cliente = clientes.find(c => c.id === orcamento.cliente_id);
    const veiculo = veiculos.find(v => v.id === orcamento.veiculo_id);
    
    let texto = `*ORÇAMENTO*\n\n`;
    texto += `*Cliente:* ${cliente?.nome || '-'}\n`;
    if (veiculo) {
      texto += `*Veículo:* ${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa}\n`;
    }
    texto += `*Data:* ${formatDate(orcamento.created_at)}\n`;
    texto += `*Validade:* ${formatDate(orcamento.validade)}\n\n`;
    texto += `*ITENS:*\n`;
    
    orcamento.items.forEach((item, index) => {
      texto += `${index + 1}. ${item.descricao}\n`;
      texto += `   ${item.quantidade}x ${formatCurrency(item.valor_unitario)} = ${formatCurrency(item.valor_total)}\n`;
    });
    
    texto += `\n*VALOR TOTAL: ${formatCurrency(orcamento.valor_total)}*`;
    
    if (orcamento.observacoes) {
      texto += `\n\n*Observações:* ${orcamento.observacoes}`;
    }
    
    navigator.clipboard.writeText(texto).then(() => {
      toast.success('Orçamento copiado para a área de transferência!');
    }).catch(() => {
      toast.error('Erro ao copiar orçamento');
    });
  };

  const handleCompartilharWhatsApp = (orcamento) => {
    const cliente = clientes.find(c => c.id === orcamento.cliente_id);
    const veiculo = veiculos.find(v => v.id === orcamento.veiculo_id);
    
    let texto = `*ORÇAMENTO*\n\n`;
    texto += `*Cliente:* ${cliente?.nome || '-'}\n`;
    if (veiculo) {
      texto += `*Veículo:* ${veiculo.marca} ${veiculo.modelo} - ${veiculo.placa}\n`;
    }
    texto += `*Validade:* ${formatDate(orcamento.validade)}\n\n`;
    texto += `*ITENS:*\n`;
    
    orcamento.items.forEach((item, index) => {
      texto += `${index + 1}. ${item.descricao} - ${formatCurrency(item.valor_total)}\n`;
    });
    
    texto += `\n*VALOR TOTAL: ${formatCurrency(orcamento.valor_total)}*`;
    
    const mensagemCodificada = encodeURIComponent(texto);
    let whatsappUrl;
    if (cliente?.telefone) {
      const telefone = cliente.telefone.replace(/\D/g, '');
      whatsappUrl = `https://wa.me/55${telefone}?text=${mensagemCodificada}`;
    } else {
      whatsappUrl = `https://wa.me/?text=${mensagemCodificada}`;
    }
    
    window.open(whatsappUrl, '_blank');
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      veiculo_id: '',
      items: [],
      observacoes: '',
      validade: ''
    });
    setVeiculosFiltrados([]);
    setCurrentItem({ tipo: 'servico', descricao: '', quantidade: 1, valor_unitario: '', valor_total: 0, desconto_tipo: 'valor', desconto_valor: '' });
    setSearchPeca('');
    setEditingOrcamento(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Se for apenas data (YYYY-MM-DD), adiciona horário ao meio-dia para evitar problemas de fuso
    if (dateString.length === 10) {
      dateString = dateString + 'T12:00:00';
    }
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pendente': return 'bg-yellow-500/10 text-yellow-500';
      case 'aprovado': return 'bg-green-500/10 text-green-500';
      case 'recusado': return 'bg-red-500/10 text-red-500';
      case 'convertido': return 'bg-electric-blue/10 text-electric-blue';
      default: return 'bg-slate-500/10 text-slate-500';
    }
  };

  const getClienteNome = (id) => {
    const cliente = clientes.find(c => c.id === id);
    return cliente ? cliente.nome : '-';
  };

  const calcularTotal = () => {
    return formData.items.reduce((acc, item) => acc + item.valor_total, 0);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="orcamentos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase tracking-tight mb-1">Orçamentos</h1>
          <p className="text-sm text-slate-500">Crie e gerencie orçamentos</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-electric-blue hover:bg-blue-600 text-white rounded-sm w-full sm:w-auto" data-testid="add-orcamento-btn">
              <Plus className="w-4 h-4 mr-2" />
              Novo Orçamento
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-[95vw] sm:max-w-2xl lg:max-w-4xl max-h-[85vh] overflow-y-auto mx-2">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-bold uppercase">
                {editingOrcamento ? 'Editar Orçamento' : 'Novo Orçamento'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Cliente</Label>
                  <div className="flex gap-2">
                    <Select 
                      value={formData.cliente_id} 
                      onValueChange={handleClienteChange}
                      required
                    >
                      <SelectTrigger className="bg-obsidian border-white/10 text-white flex-1">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent className="bg-carbon border-white/10 text-white">
                        {clientes.map(cliente => (
                          <SelectItem key={cliente.id} value={cliente.id}>{cliente.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsClienteDialogOpen(true)}
                      className="border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white px-3"
                      data-testid="novo-cliente-btn"
                    >
                      <UserPlus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Veículo</Label>
                  <Select 
                    value={formData.veiculo_id} 
                    onValueChange={(value) => setFormData({ ...formData, veiculo_id: value })}
                    disabled={!formData.cliente_id || veiculosFiltrados.length === 0}
                  >
                    <SelectTrigger className="bg-obsidian border-white/10 text-white">
                      <SelectValue placeholder={formData.cliente_id ? "Selecione" : "Cliente primeiro"} />
                    </SelectTrigger>
                    <SelectContent className="bg-carbon border-white/10 text-white">
                      {veiculosFiltrados.map(veiculo => (
                        <SelectItem key={veiculo.id} value={veiculo.id}>
                          {veiculo.placa} - {veiculo.marca} {veiculo.modelo}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Validade</Label>
                <Input
                  type="date"
                  value={formData.validade}
                  onChange={(e) => setFormData({ ...formData, validade: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  required
                />
              </div>

              {/* Busca de Peças */}
              <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
                <Label className="text-xs uppercase tracking-wider text-electric-blue mb-2 block">Buscar Peça do Estoque</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={searchPeca}
                    onChange={(e) => setSearchPeca(e.target.value)}
                    placeholder="Digite para buscar..."
                    className="bg-carbon border-white/10 text-white pl-10"
                  />
                </div>
                {filteredPecas.length > 0 && (
                  <div className="mt-2 bg-carbon border border-white/10 rounded-md max-h-32 overflow-y-auto">
                    {filteredPecas.map(peca => (
                      <button
                        key={peca.id}
                        type="button"
                        onClick={() => handleSelectPeca(peca.id)}
                        className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm flex justify-between"
                      >
                        <span className="text-white">{peca.nome}</span>
                        <span className="text-slate-400">{formatCurrency(peca.preco_venda)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Adicionar Item Manual */}
              <div className="bg-obsidian p-3 sm:p-4 rounded-md border border-white/10">
                <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Adicionar Item</Label>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
                  <div>
                    <Select value={currentItem.tipo} onValueChange={(v) => setCurrentItem({...currentItem, tipo: v})}>
                      <SelectTrigger className="bg-carbon border-white/10 text-white text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-carbon border-white/10 text-white">
                        <SelectItem value="servico">Serviço</SelectItem>
                        <SelectItem value="peca">Peça</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={currentItem.descricao}
                      onChange={(e) => setCurrentItem({...currentItem, descricao: e.target.value})}
                      placeholder="Descrição"
                      className="bg-carbon border-white/10 text-white text-sm"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      value={currentItem.quantidade}
                      onChange={(e) => setCurrentItem({...currentItem, quantidade: parseInt(e.target.value) || 1})}
                      placeholder="Qtd"
                      className="bg-carbon border-white/10 text-white text-sm"
                      min="1"
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      step="0.01"
                      value={currentItem.valor_unitario}
                      onChange={(e) => setCurrentItem({...currentItem, valor_unitario: e.target.value})}
                      placeholder="Valor"
                      className="bg-carbon border-white/10 text-white text-sm"
                    />
                  </div>
                </div>
                {/* Linha de desconto por item */}
                <div className="grid grid-cols-5 gap-2 mt-2 items-center">
                  <div className="col-span-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-400">Desconto</Label>
                  </div>
                  <div className="col-span-2">
                    <Select value={currentItem.desconto_tipo} onValueChange={(v) => setCurrentItem({...currentItem, desconto_tipo: v})}>
                      <SelectTrigger className="bg-carbon border-white/10 text-white text-sm h-9" data-testid="desconto-tipo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-carbon border-white/10 text-white">
                        <SelectItem value="valor">R$ (valor)</SelectItem>
                        <SelectItem value="percentual">% (percentual)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max={currentItem.desconto_tipo === 'percentual' ? 100 : undefined}
                      value={currentItem.desconto_valor}
                      onChange={(e) => setCurrentItem({...currentItem, desconto_valor: e.target.value})}
                      placeholder={currentItem.desconto_tipo === 'percentual' ? '0-100%' : '0,00'}
                      className="bg-carbon border-white/10 text-white text-sm h-9"
                      data-testid="desconto-valor"
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  onClick={handleAddItem}
                  disabled={!currentItem.descricao || !currentItem.valor_unitario}
                  className="w-full mt-3 bg-electric-blue hover:bg-blue-600 text-white"
                >
                  Adicionar Item
                </Button>
              </div>

              {/* Lista de Itens */}
              {formData.items.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Itens do Orçamento</Label>
                  {formData.items.map((item, index) => {
                    const subtotal = (item.quantidade || 0) * (item.valor_unitario || 0);
                    const hasDesconto = item.desconto_valor > 0 && item.desconto_tipo;
                    return (
                    <div key={item._key || item.id || index} className="flex items-center justify-between bg-obsidian p-3 rounded-md">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{item.descricao}</p>
                        <p className="text-xs text-slate-500">
                          {item.quantidade}x {formatCurrency(item.valor_unitario)}
                          {hasDesconto && (
                            <span className="ml-1 text-amber-400">
                              {' • '}Desc: {item.desconto_tipo === 'percentual' ? `${item.desconto_valor}%` : formatCurrency(item.desconto_valor)}
                            </span>
                          )}
                          {' = '}
                          {hasDesconto && (
                            <span className="line-through text-slate-600 mr-1">{formatCurrency(subtotal)}</span>
                          )}
                          <span className="text-emerald-400 font-semibold">{formatCurrency(item.valor_total)}</span>
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:text-white hover:bg-red-500/10 ml-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    );
                  })}
                  <div className="flex justify-between items-center pt-2 border-t border-white/10">
                    <span className="text-sm text-slate-400">Total:</span>
                    <span className="text-lg font-bold text-white">{formatCurrency(calcularTotal())}</span>
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Observações</Label>
                <Input
                  value={formData.observacoes}
                  onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  placeholder="Opcional"
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)} className="text-slate-400">
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                  {editingOrcamento ? 'Salvar Alterações' : 'Criar Orçamento'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Dialog Novo Cliente */}
        <Dialog open={isClienteDialogOpen} onOpenChange={(open) => {
          setIsClienteDialogOpen(open);
          if (!open) {
            setNovoCliente({ nome: '', cpf_cnpj: '', telefone: '', email: '', endereco: '' });
            setNovosVeiculos([]);
            setShowVeiculoForm(false);
          }
        }}>
          <DialogContent className="bg-carbon border-white/10 text-white max-w-[95vw] sm:max-w-2xl mx-2 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase">Novo Cliente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCadastrarCliente} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Nome Completo *</Label>
                <Input
                  value={novoCliente.nome}
                  onChange={(e) => setNovoCliente({ ...novoCliente, nome: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">CPF/CNPJ</Label>
                  <Input
                    value={novoCliente.cpf_cnpj}
                    onChange={(e) => setNovoCliente({ ...novoCliente, cpf_cnpj: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Telefone</Label>
                  <Input
                    value={novoCliente.telefone}
                    onChange={(e) => setNovoCliente({ ...novoCliente, telefone: e.target.value })}
                    className="bg-obsidian border-white/10 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={novoCliente.email}
                  onChange={(e) => setNovoCliente({ ...novoCliente, email: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Endereço</Label>
                <Input
                  value={novoCliente.endereco}
                  onChange={(e) => setNovoCliente({ ...novoCliente, endereco: e.target.value })}
                  className="bg-obsidian border-white/10 text-white"
                />
              </div>

              {/* Seção de Veículos */}
              <div className="border-t border-white/10 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Veículos do Cliente (Opcional)</Label>
                  {!showVeiculoForm && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowVeiculoForm(true)}
                      className="text-electric-blue hover:text-white hover:bg-electric-blue/10"
                    >
                      <Car className="w-4 h-4 mr-1" />
                      Adicionar Veículo
                    </Button>
                  )}
                </div>

                {/* Lista de veículos adicionados */}
                {novosVeiculos.length > 0 && (
                  <div className="space-y-2 mb-3">
                    {novosVeiculos.map((veiculo, index) => (
                      <div key={veiculo.placa || `veic-${index}`} className="flex items-center justify-between bg-obsidian p-3 rounded-sm">
                        <div>
                          <p className="text-sm text-white font-medium">{veiculo.marca} {veiculo.modelo}</p>
                          <p className="text-xs text-slate-500">
                            {veiculo.placa} {veiculo.ano && `• ${veiculo.ano}`} {veiculo.cor && `• ${veiculo.cor}`}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveVeiculo(index)}
                          className="text-red-500 hover:text-white hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Formulário de veículo */}
                {showVeiculoForm && (
                  <div className="bg-obsidian p-4 rounded-md space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Placa *</Label>
                        <Input
                          value={veiculoForm.placa}
                          onChange={(e) => setVeiculoForm({ ...veiculoForm, placa: e.target.value.toUpperCase() })}
                          className="bg-carbon border-white/10 text-white"
                          />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Cor</Label>
                        <Input
                          value={veiculoForm.cor}
                          onChange={(e) => setVeiculoForm({ ...veiculoForm, cor: e.target.value })}
                          className="bg-carbon border-white/10 text-white"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Marca *</Label>
                        <Input
                          value={veiculoForm.marca}
                          onChange={(e) => setVeiculoForm({ ...veiculoForm, marca: e.target.value })}
                          className="bg-carbon border-white/10 text-white"
                          />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Modelo *</Label>
                        <Input
                          value={veiculoForm.modelo}
                          onChange={(e) => setVeiculoForm({ ...veiculoForm, modelo: e.target.value })}
                          className="bg-carbon border-white/10 text-white"
                          />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-400">Ano</Label>
                        <Input
                          type="number"
                          value={veiculoForm.ano}
                          onChange={(e) => setVeiculoForm({ ...veiculoForm, ano: e.target.value })}
                          className="bg-carbon border-white/10 text-white"
                          />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowVeiculoForm(false);
                          setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
                        }}
                        className="text-slate-400"
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddVeiculo}
                        disabled={!veiculoForm.placa || !veiculoForm.marca || !veiculoForm.modelo}
                        className="bg-electric-blue hover:bg-blue-600 text-white"
                      >
                        Adicionar
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="ghost" 
                  onClick={() => {
                    setIsClienteDialogOpen(false);
                    setNovoCliente({ nome: '', cpf_cnpj: '', telefone: '', email: '', endereco: '' });
                    setNovosVeiculos([]);
                    setShowVeiculoForm(false);
                  }} 
                  className="text-slate-400"
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-electric-blue hover:bg-blue-600 text-white">
                  Cadastrar Cliente
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lista de Orçamentos */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        {orcamentos.length === 0 ? (
          <div className="bg-carbon border border-white/5 rounded-md p-8 sm:p-12 text-center">
            <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-sm sm:text-base text-slate-500">Nenhum orçamento cadastrado</p>
          </div>
        ) : (
          orcamentos.map((orcamento) => (
            <div key={orcamento.id} className="bg-carbon border border-white/5 rounded-md p-4 sm:p-6 hover:border-white/20 transition-colors" data-testid={`orcamento-card-${orcamento.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-semibold text-white truncate">{getClienteNome(orcamento.cliente_id)}</h3>
                  <p className="text-xs sm:text-sm text-slate-500">
                    {formatDate(orcamento.created_at)} • Val: {formatDate(orcamento.validade)}
                  </p>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(orcamento)}
                      className="text-slate-400 hover:text-white hover:bg-white/10 p-2"
                      data-testid={`editar-${orcamento.id}`}
                      title="Editar orçamento"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopiarOrcamento(orcamento)}
                      className="text-electric-blue hover:text-white hover:bg-electric-blue/10 p-2"
                      data-testid={`copiar-${orcamento.id}`}
                      title="Copiar orçamento"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCompartilharWhatsApp(orcamento)}
                      className="text-green-500 hover:text-white hover:bg-green-500/10 p-2"
                      data-testid={`whatsapp-${orcamento.id}`}
                      title="Compartilhar via WhatsApp"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-sm text-[10px] sm:text-xs font-medium uppercase tracking-wider ${getStatusColor(orcamento.status)}`}>
                    {orcamento.status}
                  </span>
                  <span className="text-lg sm:text-xl font-bold text-white">{formatCurrency(orcamento.valor_total)}</span>
                </div>
              </div>

              <div className="space-y-1 sm:space-y-2 mb-4">
                {orcamento.items.slice(0, 3).map((item, index) => (
                  <div key={item._key || `${orcamento.id}-item-${index}`} className="flex items-center justify-between text-xs sm:text-sm">
                    <div className="flex-1 min-w-0 truncate">
                      <span className="text-slate-400">{item.quantidade}x</span>
                      <span className="text-white ml-1 sm:ml-2">{item.descricao}</span>
                    </div>
                    <span className="text-slate-300 ml-2 flex-shrink-0">{formatCurrency(item.valor_total)}</span>
                  </div>
                ))}
                {orcamento.items.length > 3 && (
                  <p className="text-xs text-slate-500">+{orcamento.items.length - 3} itens...</p>
                )}
              </div>

              {orcamento.status === 'pendente' && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      size="sm"
                      onClick={() => handleAprovar(orcamento.id)}
                      className="bg-green-500 hover:bg-green-600 text-white text-xs sm:text-sm"
                      data-testid={`aprovar-${orcamento.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Aprovar
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleRecusar(orcamento.id)}
                      variant="ghost"
                      className="text-red-500 hover:text-white hover:bg-red-500/10 text-xs sm:text-sm"
                      data-testid={`recusar-${orcamento.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Recusar
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(orcamento.id)}
                    className="text-alert-red hover:text-white hover:bg-alert-red/10"
                    data-testid={`delete-orcamento-${orcamento.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {orcamento.status === 'aprovado' && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-white/5">
                  <Button
                    size="sm"
                    onClick={() => handleConverter(orcamento.id)}
                    className="bg-electric-blue hover:bg-blue-600 text-white text-xs sm:text-sm w-full sm:w-auto"
                    data-testid={`converter-${orcamento.id}`}
                  >
                    <ArrowRight className="w-4 h-4 mr-1" />
                    Converter em OS
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(orcamento.id)}
                    className="text-alert-red hover:text-white hover:bg-alert-red/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {(orcamento.status === 'recusado' || orcamento.status === 'convertido') && (
                <div className="flex justify-end pt-4 border-t border-white/5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(orcamento.id)}
                    className="text-alert-red hover:text-white hover:bg-alert-red/10"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Excluir
                  </Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Orcamentos;
